/**
 * Webhooks Management - Platform event webhooks
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Webhook as WebhookIcon, Play, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const AVAILABLE_EVENTS = [
  { id: 'ptt.start', label: 'PTT Inicio', desc: 'Alguien empieza a transmitir' },
  { id: 'ptt.stop', label: 'PTT Fin', desc: 'Termina transmisión' },
  { id: 'ptt.transcription', label: 'Transcripción', desc: 'Nueva transcripción disponible' },
  { id: 'user.joined', label: 'Usuario Unido', desc: 'Usuario se une a canal' },
  { id: 'user.connected', label: 'Usuario Conectado', desc: 'Usuario conectado al sistema' },
  { id: 'channel.created', label: 'Canal Creado', desc: 'Nuevo canal creado' },
  { id: 'panic.alert', label: 'Alerta de Pánico', desc: 'Alerta de pánico activada' },
  { id: '*', label: 'Todos los eventos', desc: 'Recibe todos los eventos' },
];

function Modal({ title, onClose, children, maxWidth = 'max-w-lg' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.40)', backdropFilter: 'blur(4px)' }}>
      <div className={`w-full ${maxWidth} rounded-2xl p-6 max-h-[90vh] overflow-y-auto`}
        style={{ background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(0,122,255,0.20)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-n-800 text-base">{title}</h3>
          <button onClick={onClose} className="text-n-500 hover:text-n-700"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <button onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-s-blue' : 'bg-n-300'}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

const emptyForm = { name: '', url: '', secret: '', events: [], active: true };

export default function Webhooks() {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [logs, setLogs] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/webhooks');
      setHooks(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setErr('');
    setModal('create');
  };

  const openEdit = (h) => {
    setSelected(h);
    setForm({ name: h.name, url: h.url, secret: h.secret || '', events: h.events || [], active: h.active });
    setErr('');
    setModal('edit');
  };

  const openDelete = (h) => { setSelected(h); setErr(''); setModal('delete'); };

  const openLogs = async (h) => {
    setSelected(h);
    try {
      const res = await axios.get(`/api/admin/webhooks/${h.id}/logs`);
      setLogs(res.data);
    } catch (e) { setLogs([]); }
    setModal('logs');
  };

  const toggleEvent = (eventId) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(eventId)
        ? f.events.filter(e => e !== eventId)
        : [...f.events, eventId],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) { setErr('Nombre y URL son requeridos'); return; }
    setSaving(true);
    try {
      if (modal === 'create') {
        await axios.post('/api/admin/webhooks', form);
      } else {
        await axios.put(`/api/admin/webhooks/${selected.id}`, form);
      }
      await load();
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`/api/admin/webhooks/${selected.id}`);
      await load();
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Error');
    } finally { setSaving(false); }
  };

  const handleTest = async (h) => {
    setTestResult('Enviando...');
    try {
      await axios.post(`/api/admin/webhooks/${h.id}/test`);
      setTestResult('✅ Payload de prueba enviado');
    } catch (e) {
      setTestResult('❌ Error al enviar: ' + (e.response?.data?.error || e.message));
    }
    setTimeout(() => setTestResult(''), 3000);
  };

  const toggleActive = async (h) => {
    try {
      await axios.put(`/api/admin/webhooks/${h.id}`, { active: !h.active });
      await load();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-n-800">Webhooks</h1>
          <p className="text-sm text-n-500 mt-0.5">Integra eventos de la plataforma con servicios externos</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-n-800 transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
          <Plus size={15} /> Nuevo Webhook
        </button>
      </div>

      {testResult && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm text-n-800"
          style={{ background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,122,255,0.20)' }}>
          {testResult}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-n-500 text-sm">Cargando...</div>
      ) : hooks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-n-500 text-sm">
          <WebhookIcon size={32} className="mb-3 opacity-30" />
          <p>No hay webhooks configurados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {hooks.map(h => (
            <div key={h.id} className="px-5 py-4 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.82)', border: `1px solid ${h.active ? 'rgba(0,122,255,0.12)' : 'rgba(255,255,255,0.04)'}` }}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-n-800 text-sm">{h.name}</span>
                    {h.failCount > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full text-s-red bg-s-red/10 border border-red-500/20 flex items-center gap-1">
                        <AlertCircle size={10} /> {h.failCount} fallos
                      </span>
                    )}
                    {h.lastTriggered && (
                      <span className="text-xs text-n-400 flex items-center gap-1">
                        <Clock size={10} /> {new Date(h.lastTriggered).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-n-500 mt-0.5 truncate">{h.url}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(h.events || []).map(e => (
                      <span key={e} className="text-xs px-1.5 py-0.5 rounded text-n-600 bg-white/5">{e}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ToggleSwitch checked={h.active} onChange={() => toggleActive(h)} />
                  <button onClick={() => handleTest(h)}
                    className="p-2 rounded-lg text-n-500 hover:text-s-green hover:bg-s-green/10 transition-all" title="Probar">
                    <Play size={13} />
                  </button>
                  <button onClick={() => openLogs(h)}
                    className="p-2 rounded-lg text-n-500 hover:text-s-blue hover:bg-s-blue/10 transition-all" title="Logs">
                    <Clock size={13} />
                  </button>
                  <button onClick={() => openEdit(h)}
                    className="p-2 rounded-lg text-n-500 hover:text-s-blue hover:bg-s-blue/10 transition-all">
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => openDelete(h)}
                    className="p-2 rounded-lg text-n-500 hover:text-s-red hover:bg-s-red/10 transition-all">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {(modal === 'create' || modal === 'edit') && (
        <Modal title={modal === 'create' ? 'Nuevo Webhook' : 'Editar Webhook'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Nombre</label>
              <input className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none"
                style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
                placeholder="Mi integración" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">URL</label>
              <input className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none"
                style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
                placeholder="https://..." value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Secret (opcional)</label>
              <input className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none"
                style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
                placeholder="firma HMAC-SHA256" value={form.secret}
                onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-2">Eventos</label>
              <div className="space-y-1.5">
                {AVAILABLE_EVENTS.map(ev => (
                  <label key={ev.id} className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer transition-all hover:bg-white/5">
                    <input type="checkbox" checked={form.events.includes(ev.id)}
                      onChange={() => toggleEvent(ev.id)}
                      className="w-4 h-4 rounded accent-brand-500" />
                    <div>
                      <div className="text-sm text-n-800">{ev.label}</div>
                      <div className="text-xs text-n-500">{ev.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between px-1">
              <span className="text-sm text-n-600">Activo</span>
              <ToggleSwitch checked={form.active} onChange={v => setForm(f => ({ ...f, active: v }))} />
            </div>
            {err && <p className="text-xs text-s-red">{err}</p>}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600 border border-slate-700 hover:border-slate-500 transition-all">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-800 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === 'delete' && (
        <Modal title="Eliminar Webhook" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <p className="text-sm text-n-600">¿Eliminar el webhook <span className="text-n-800 font-semibold">{selected?.name}</span>?</p>
            {err && <p className="text-xs text-s-red">{err}</p>}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600 border border-slate-700">Cancelar</button>
              <button onClick={handleDelete} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#FF375F,#FF3B30)' }}>
                {saving ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Logs Modal */}
      {modal === 'logs' && (
        <Modal title={`Logs: ${selected?.name}`} onClose={() => setModal(null)} maxWidth="max-w-2xl">
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-sm text-n-500 text-center py-8">No hay logs aún</p>
            ) : logs.map((log, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2.5 rounded-xl text-xs"
                style={{ background: log.success ? 'rgba(52,199,89,0.06)' : 'rgba(255,107,107,0.05)', border: `1px solid ${log.success ? 'rgba(52,199,89,0.12)' : 'rgba(255,55,95,0.12)'}` }}>
                {log.success
                  ? <CheckCircle size={13} className="text-s-green flex-shrink-0 mt-0.5" />
                  : <AlertCircle size={13} className="text-s-red flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-n-700 font-mono">{log.event}</span>
                    {log.status && <span className="text-n-500">HTTP {log.status}</span>}
                    {log.error && <span className="text-s-red truncate">{log.error}</span>}
                  </div>
                  <div className="text-n-400 mt-0.5">{new Date(log.timestamp).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
